/**
 * Which nav entry the current route is on -- the one answer `ShellNav`'s own
 * highlight and the topbar's screen name both read, so they cannot disagree
 * about where the reader is.
 */

import { useMatchRoute } from '@tanstack/react-router';

/** The fields the active-entry lookup needs. `ShellNavItem` and
 *  `OpsNavEntry` (already resolved to a label) both satisfy this. */
export interface TopbarEntry {
  path: string;
  params?: Record<string, string>;
  /** Present only on an entry whose route it also takes to be "on" this
   *  entry, e.g. two children of one screen reached by `?view=`. See
   *  `activeEntry`'s own doc comment for how it breaks a path tie. */
  search?: Record<string, string>;
  /** A second route this entry is current on (`NavEntry.activeOn`). */
  activeOn?: string;
  label: string;
}

/**
 * `entry.params` as callers build it carries every param the current URL
 * resolved (`navParams`'s `tenantId` and `campaignId` alike), not only the
 * ones `path` declares. `matchRoute`'s own equality check is partial on its
 * side but not on this one -- a key present here with no counterpart in the
 * matched route's own params (a campaign path has no `$tenantId`) fails the
 * whole match, not just that key. Scoping to `path`'s own `$name` segments
 * is what makes passing `params` safe at all.
 */
function ownParams(
  path: string,
  params: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (params === undefined) return undefined;
  const names = (path.match(/\$[a-zA-Z0-9_]+/g) ?? []).map((segment) => segment.slice(1));
  const scoped: Record<string, string> = {};
  for (const name of names) if (params[name] !== undefined) scoped[name] = params[name];
  return scoped;
}

/**
 * Whether the current route is on or below `entry`, the one match rule
 * `ShellNav`'s own highlight and the topbar's screen name both apply --
 * before this they diverged on whether an entry's `params` reached
 * `matchRoute`, so the two could rank the same route differently.
 *
 * A fuzzy match against `/` answers false for every path below it, so the
 * one entry that is an ancestor of the whole plane needs this exact-path
 * escape hatch. `exact` asks instead whether the route on screen is the
 * entry's own, not one below it.
 */
export function matchesRoute(
  matchRoute: ReturnType<typeof useMatchRoute>,
  entry: TopbarEntry,
  options: { exact?: boolean } = {},
): boolean {
  const exact = options.exact === true;
  const on = (path: string) =>
    matchRoute({
      to: path,
      params: ownParams(path, entry.params),
      search: entry.search,
      fuzzy: !exact,
    } as never) !== false;
  return (
    (entry.path === '/' && !exact) ||
    on(entry.path) ||
    (entry.activeOn !== undefined && on(entry.activeOn))
  );
}

/** How deep an entry reaches: its `activeOn` route counts, or a picker entry
 *  would lose to the list above it on the very page it opened. */
function span(entry: TopbarEntry): number {
  return entry.activeOn === undefined
    ? entry.path.length
    : Math.max(entry.path.length, entry.activeOn.length);
}

/**
 * Which entry the current route belongs to, by longest matching path, then --
 * among entries tied on that path, such as two `search`-scoped children of
 * one screen -- by whichever names more `search` keys. A plain path match
 * cannot tell those two apart on its own, since neither is a prefix of the
 * other; the one whose declared `search` the current URL actually carries is
 * the more specific answer.
 *
 * A fuzzy match alone is not the whole rule either, because an entry
 * registered at a plane's root is an ancestor of every sibling; nor is an
 * exact match, since some destinations are reached from a row and have no
 * entry of their own. Longest-path-first settles both -- see `ShellNav.tsx`'s
 * own comment, which this replaces so the two stop being separate
 * implementations of one rule.
 */
export function activeEntry<T extends TopbarEntry>(
  matches: (entry: T) => boolean,
  entries: readonly T[],
): T | null {
  let best: T | null = null;
  for (const entry of entries) {
    if (!matches(entry)) continue;
    if (best === null) {
      best = entry;
      continue;
    }
    if (span(entry) !== span(best)) {
      if (span(entry) > span(best)) best = entry;
      continue;
    }
    if (Object.keys(entry.search ?? {}).length > Object.keys(best.search ?? {}).length) {
      best = entry;
    }
  }
  return best;
}
